import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Empty, Progress, Select, Typography, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { TAIModelBuilderDatasetEntry, TAIModelBuilderLessonConfig } from '../../../models/courses/aiModelBuilder';
import './AIModelBuilderLessonPlayer.css';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';
import { useUserApi } from '../../../api/user/userApp';

type LanguageKey = 'fr' | 'en' | 'ar';

type ModelBuilderItem = {
  id: string;
  label: string;
  expectedKey: string | null;
  isNoise: boolean;
};

type ModelBuilderTarget = {
  id: string;
  label: string;
};

interface AIModelBuilderLessonPlayerProps {
  config: TAIModelBuilderLessonConfig;
  onComplete: () => void;
  statement?: string;
  lessonId?: number; // needed to load/save user_answers
}

// Normalize any incoming feature/noise value into a clean string path or text
const normalizeValue = (val: unknown): string => {
  if (val == null) return '';
  if (typeof val === 'string') {
    // Trim whitespace and surrounding quotes if any
    const trimmed = val.trim().replace(/^['"]|['"]$/g, '');
    // Normalize Windows-style backslashes to URL-friendly slashes
    return trimmed.replace(/\\/g, '/');
  }
  if (typeof val === 'object') {
    // Support common shapes from media pickers or manual JSON edits
    const obj = val as Record<string, unknown>;
    const candidate = obj.file_path || obj.path || obj.url || obj.src || obj.value;
    if (typeof candidate === 'string') return candidate.trim();
  }
  try {
    // If a JSON string slipped through
    const parsed = JSON.parse(String(val));
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      const candidate = obj.file_path || obj.path || obj.url || obj.src || obj.value;
      if (typeof candidate === 'string') return candidate.trim();
    }
  } catch (_) {
    // noop
  }
  return String(val);
};

const getLanguageKey = (language: string): LanguageKey => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('ar')) return 'ar';
  return 'en';
};

const getFeaturesByLanguage = (entry: TAIModelBuilderDatasetEntry, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return entry.features_fr ?? entry.features_en ?? entry.features_ar ?? [];
    case 'ar':
      return entry.features_ar ?? entry.features_en ?? entry.features_fr ?? [];
    default:
      return entry.features_en ?? entry.features_fr ?? entry.features_ar ?? [];
  }
};

const getNoiseByLanguage = (config: TAIModelBuilderLessonConfig, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return config.bruit_fr ?? config.bruit_en ?? config.bruit_ar ?? [];
    case 'ar':
      return config.bruit_ar ?? config.bruit_en ?? config.bruit_fr ?? [];
    default:
      return config.bruit_en ?? config.bruit_fr ?? config.bruit_ar ?? [];
  }
};

const shuffleArray = <T,>(arr: T[]): T[] =>
  arr
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);

const AIModelBuilderLessonPlayer: React.FC<AIModelBuilderLessonPlayerProps> = ({ config, onComplete, statement, lessonId }) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageKey(i18n.language || 'en');
  const { getUserAnswerById, saveUserAnswerById } = useUserApi();

  // Hash de configuration pour invalider un ancien workspace en cas de changement
  const configHash = useMemo(() => {
    try {
      const base = JSON.stringify({
        dataset: config?.dataset || {},
        bruit_fr: config?.bruit_fr || [],
        bruit_en: config?.bruit_en || [],
        bruit_ar: config?.bruit_ar || [],
        data_type: config?.data_type || {},
        statement: config?.statement || '',
      });
      return base.length;
    } catch {
      return 0;
    }
  }, [config]);

  const allNoiseItems = useMemo(
    () => getNoiseByLanguage(config, language).map((v) => normalizeValue(v)),
    [config, language]
  );

  const { targets, initialAssignments } = useMemo(() => {
    const entries = Object.entries(config.dataset || {});
    const builtTargets: ModelBuilderTarget[] = entries.map(([id, entry]) => ({ id, label: normalizeValue(entry.key || id) }));

    const assignments: Record<string, ModelBuilderItem[]> = {};
    const pool: ModelBuilderItem[] = [];

    entries.forEach(([id, entry]) => {
      const features = getFeaturesByLanguage(entry, language).map((v) => normalizeValue(v));
      features.forEach((feature, index) => {
        pool.push({
          id: `${id}-feature-${index}`,
          label: feature,
          expectedKey: id,
          isNoise: false,
        });
      });
    });

    allNoiseItems.forEach((noise, index) => {
      pool.push({
        id: `noise-${index}`,
        label: noise,
        expectedKey: null,
        isNoise: true,
      });
    });

    const shuffledPool = shuffleArray(pool);
    const targetCount = Math.max(1, builtTargets.length);

    shuffledPool.forEach((item, index) => {
      const targetIndex = index % targetCount;
      const targetId = builtTargets[targetIndex]?.id;
      if (!targetId) return;
      assignments[targetId] = [...(assignments[targetId] || []), item];
    });

    return {
      targets: builtTargets,
      initialAssignments: assignments,
    };
  }, [config.dataset, allNoiseItems, language]);

  const [assignments, setAssignments] = useState<Record<string, ModelBuilderItem[]>>(initialAssignments);
  const [noiseBin, setNoiseBin] = useState<ModelBuilderItem[]>([]);
  const [selectedDataType, setSelectedDataType] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [isTraining, setIsTraining] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Tracks an active drag: blocks async workspace restores from replacing the
  // lists mid-drag, and toggles the CSS class that suppresses hover effects
  // on the dragged clone
  const isDraggingRef = useRef(false);
  const [isDragActive, setIsDragActive] = useState(false);

  // Load existing user answer workspace if available
  useEffect(() => {
    if (!lessonId) return;
    getUserAnswerById(lessonId)
      .then((resp: any) => {
        if (isDraggingRef.current) return; // never replace lists under an active drag
        const workspace: string | undefined = resp?.data?.workspace;
        if (!workspace) return;
        try {
          const parsed = JSON.parse(workspace);
          if (!parsed || parsed.configHash !== configHash) {
            return; // configuration changed: skip restore
          }
          if (parsed && typeof parsed === 'object') {
            const byId: Map<string, ModelBuilderItem> = new Map();
            Object.values(initialAssignments).forEach((arr) => {
              (arr as ModelBuilderItem[]).forEach((it) => byId.set(it.id, it));
            });
            if (parsed.assignments && typeof parsed.assignments === 'object') {
              const restored: Record<string, ModelBuilderItem[]> = {} as any;
              Object.keys(parsed.assignments).forEach((k) => {
                const arr = Array.isArray(parsed.assignments[k]) ? parsed.assignments[k] : [];
                restored[k] = arr.map((x: any) => byId.get(x.id)).filter(Boolean) as ModelBuilderItem[];
              });
              if (Object.keys(restored).length) setAssignments((prev) => ({ ...prev, ...restored }));
            }
            if (Array.isArray(parsed.noiseBin)) {
              const restoredNoise: ModelBuilderItem[] = [];
              parsed.noiseBin.forEach((x: any) => {
                const it = byId.get(x.id);
                if (it) restoredNoise.push(it);
              });
              if (restoredNoise.length) setNoiseBin(restoredNoise);
            }
            if (typeof parsed.selectedDataType === 'string') {
              setSelectedDataType(parsed.selectedDataType);
            }
          }
        } catch (_) {
          // ignore malformed workspace
        }
      })
      .catch(() => {/* ignore */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, initialAssignments]);

  useEffect(() => {
    setAssignments(initialAssignments);
    setNoiseBin([]);
    setProgress(0);
    setAccuracy(null);
    setIsTraining(false);
  }, [initialAssignments]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const getList = useCallback(
    (droppableId: string) => {
      if (droppableId === 'noise-bin') return noiseBin;
      return assignments[droppableId] || [];
    },
    [assignments, noiseBin]
  );

  const setList = useCallback((droppableId: string, items: ModelBuilderItem[]) => {
    if (droppableId === 'noise-bin') {
      setNoiseBin(items);
    } else {
      setAssignments((prev) => ({
        ...prev,
        [droppableId]: items,
      }));
    }
  }, []);  

  const onDragStart = useCallback(() => {
    isDraggingRef.current = true;
    setIsDragActive(true);
  }, []);

  const onDragEnd = useCallback((result: DropResult) => {
    isDraggingRef.current = false;
    setIsDragActive(false);
    if (!result.destination) return;

    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    const sourceItems = Array.from(getList(result.source.droppableId));
    if (result.source.index < 0 || result.source.index >= sourceItems.length) return;

    const [moved] = sourceItems.splice(result.source.index, 1);
    if (!moved) return;

    const destinationItems = Array.from(getList(result.destination.droppableId));
    if (result.destination.index < 0 || result.destination.index > destinationItems.length) return;

    if (result.source.droppableId === result.destination.droppableId) {
      destinationItems.splice(result.destination.index, 0, moved);
      setList(result.destination.droppableId, destinationItems);
    } else {
      destinationItems.splice(result.destination.index, 0, moved);
      setList(result.source.droppableId, sourceItems);
      setList(result.destination.droppableId, destinationItems);
    }
  }, [getList, setList]);

  const handleStartTraining = () => {
    if (isTraining) return;

    if (!selectedDataType) {
      message.warning(t('aiModelBuilder.student.messages.selectOptions'));
      return;
    }

    const selectedType = config.data_type?.[selectedDataType];
    if (!selectedType || (selectedType.status ?? 'false') !== 'true') {
      message.error(t('aiModelBuilder.student.messages.invalidDataType'));
      setAccuracy(null);
      return;
    }

    setIsTraining(true);
    setProgress(0);
    setAccuracy(null);

    const start = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const percent = Math.min(100, Math.round((elapsed / 10000) * 100));
      setProgress(percent);
      if (percent >= 100) {
        clearInterval(timerRef.current as NodeJS.Timeout);
        evaluateAccuracy();
        setIsTraining(false);
      }
    }, 300);
  };

  const evaluateAccuracy = async () => {
    const allTargetIds = Object.keys(assignments);
    const lists = [...allTargetIds.map((id) => assignments[id] || []), noiseBin];
    const totalItems = lists.reduce((sum, arr) => sum + arr.length, 0);
    if (totalItems === 0) {
      setAccuracy(0);
      message.info(t('aiModelBuilder.student.messages.emptyData'));
      return;
    }

    let correct = 0;
    allTargetIds.forEach((targetId) => {
      (assignments[targetId] || []).forEach((item) => {
        if (!item.isNoise && item.expectedKey === targetId) {
          correct += 1;
        }
      });
    });

    noiseBin.forEach((item) => {
      if (item.isNoise) correct += 1;
    });

    const computedAccuracy = Math.round((correct / totalItems) * 100);
    setAccuracy(computedAccuracy);

    // Save user answer workspace in all cases (partial or success)
    try {
      if (lessonId) {
        const payload = {
          workspace: JSON.stringify({
            configHash,
            assignments: Object.fromEntries(
              Object.entries(assignments).map(([k, arr]) => [k, arr.map((it) => ({ id: it.id }))])
            ),
            noiseBin: noiseBin.map((it) => ({ id: it.id })),
            selectedDataType,
            accuracy: computedAccuracy,
          }),
        };
        await saveUserAnswerById(lessonId, payload);
      }
    } catch (e) {
      // ignore save error
    }

    if (computedAccuracy === 100) {
      onComplete();
    } else {
      message.warning(t('aiModelBuilder.student.messages.trainingImprove', { accuracy: computedAccuracy }));
    }
  };

  const isImagePath = (raw: string) => {
    const v = (raw || '').trim().toLowerCase();
    return (
      v.startsWith('data:image/') ||
      /\.(png|jpe?g|gif|svg|webp)(\?|#|$)/i.test(v) ||
      v.includes('/images/') || v.includes('/image/') || v.includes('/uploads/')
    );
  };
  const isAudioPath = (raw: string) => {
    const v = (raw || '').trim().toLowerCase();
    return (
      v.startsWith('data:audio/') ||
      /\.(mp3|wav|ogg|m4a)(\?|#|$)/i.test(v) ||
      v.includes('/sounds/') || v.includes('/audio/')
    );
  };

  const renderItem = (item: ModelBuilderItem, index: number) => {
    const label = normalizeValue(item.label);
    return (
      <Draggable draggableId={item.id} index={index} key={item.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="aimb-draggable"
            style={{
              ...provided.draggableProps.style,
              opacity: snapshot.isDragging ? 0.9 : 1,
              ...(snapshot.isDragging && {
                boxShadow: '0 8px 24px rgba(41, 146, 191, 0.3)',
              }),
            }}
          >
            {isImagePath(label) ? (
              <img src={getImagePath(label)} alt="feature" className="aimb-draggable-media" draggable={false} />
            ) : isAudioPath(label) ? (
              <audio
                src={getSoundPath(label)}
                controls
                className="aimb-draggable-media"
                draggable={false}
                // react-beautiful-dnd starts drags from mousedown/touchstart:
                // stop those so clicking the audio controls doesn't lift the item
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              />
            ) : (
              <Typography.Text ellipsis={{ tooltip: label }}>
                {label}
              </Typography.Text>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  const renderDropZone = (droppableId: string, title: string, items: ModelBuilderItem[]) => (
    <Droppable droppableId={droppableId} ignoreContainerClipping>
      {(provided, snapshot) => {
        const normalizedTitle = normalizeValue(title);
        const showImage = !!normalizedTitle && isImagePath(normalizedTitle);
        const showAudio = !!normalizedTitle && isAudioPath(normalizedTitle);
        const isTrashBin = droppableId === 'noise-bin';
        return (
          <Card
            title={
              isTrashBin ? (
                <div className="aimb-trash-title">
                  <DeleteOutlined className="aimb-trash-icon" />
                  <span>{normalizedTitle}</span>
                </div>
              ) : showImage || showAudio ? (
                undefined
              ) : (
                normalizedTitle
              )
            }
            className={`aimb-dropzone ${isTrashBin ? 'aimb-dropzone--trash' : ''} ${snapshot.isDraggingOver ? 'aimb-dropzone--active' : ''}`}
          >
            {showImage && (
              <div className="aimb-media-preview">
                <img src={getImagePath(normalizedTitle)} alt="key" draggable={false} />
              </div>
            )}
            {showAudio && (
              <div className="aimb-media-preview">
                <audio src={getSoundPath(normalizedTitle)} controls draggable={false} onPointerDown={(e) => e.stopPropagation()} />
              </div>
            )}
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="aimb-dropzone-content"
            >
              {items.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={isTrashBin ? t('aiModelBuilder.student.emptyTrash') : undefined}
                />
              ) : (
                items.map((item, index) => renderItem(item, index))
              )}
              {provided.placeholder}
            </div>
          </Card>
        );
      }}
    </Droppable>
  );


  const availableDataTypes = useMemo(
    () => Object.entries(config.data_type || {}),
    [config.data_type],
  );

  const activeDataTypes = useMemo(
    () => availableDataTypes.filter(([, value]) => (value.status ?? 'false') === 'true'),
    [availableDataTypes],
  );

  useEffect(() => {
    if (!selectedDataType && activeDataTypes.length > 0) {
      setSelectedDataType(activeDataTypes[0][0]);
    }
  }, [selectedDataType, activeDataTypes]);

  if (!targets.length) {
    return <Empty description={t('aiModelBuilder.student.empty')} />;
  }

  return (
    <div className={`aimb-player aimb-player--builder${isDragActive ? ' aimb-player--dragging' : ''}`}>
      {statement || config.statement ? (
        <div className="aimb-statement">
          {statement || config.statement}
        </div>
      ) : null}
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="aimb-layout">
          <div className="aimb-column aimb-column--keys">
            <div className="aimb-keys-grid">
              {targets.map((target) => renderDropZone(target.id, target.label, assignments[target.id] || []))}
            </div>
          </div>
          <div className="aimb-column aimb-column--right">
            <div className="aimb-right-section">
              <div className="aimb-trash-wrapper">
                {renderDropZone('noise-bin', t('aiModelBuilder.student.noiseBin'), noiseBin)}
              </div>
              <div className="aimb-column aimb-column--controls">
                <Card className="aimb-control-card" title={t('aiModelBuilder.student.dataType')}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder={t('aiModelBuilder.student.selectDataType')}
                    size="large"
                    options={availableDataTypes.map(([id, value]) => ({
                      value: id,
                      label: value[`title_${language}` as keyof typeof value] || value.title_en || id,
                    }))}
                    onChange={(val) => setSelectedDataType(val)}
                    value={selectedDataType}
                  />
                </Card>
                <Card className="aimb-control-card aimb-training-card">
                  <div className="aimb-training-content">
                    <Button
                      block
                      size="large"
                      className="aimb-train-button"
                      onClick={handleStartTraining}
                      disabled={isTraining || !selectedDataType}
                      style={{
                        background: 'linear-gradient(135deg, #2992bf, #8bbe1e)',
                        color: '#ffffff',
                        border: 'none',
                        opacity: selectedDataType ? 1 : 0.7,
                        fontWeight: 800,
                        fontSize: '18px',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {isTraining ? (
                        <span className="aimb-training-loading">
                          <span className="aimb-loading-spinner"></span>
                          {t('aiModelBuilder.student.analyzing')}
                        </span>
                      ) : (
                        t('aiModelBuilder.student.train')
                      )}
                    </Button>
                    {isTraining && (
                      <div className="aimb-analysis-container">
                        <Progress 
                          percent={progress} 
                          className="aimb-progress"
                          strokeColor={{
                            '0%': '#2992bf',
                            '100%': '#8bbe1e',
                          }}
                          showInfo={false}
                        />
                        <Typography.Text className="aimb-progress-text">
                          {t('aiModelBuilder.student.analyzingProgress', { value: progress })}
                        </Typography.Text>
                      </div>
                    )}
                    {accuracy !== null && !isTraining && (
                      <div className={`aimb-accuracy ${accuracy === 100 ? 'aimb-accuracy--success' : 'aimb-accuracy--warning'}`}>
                        <Typography.Text strong>
                          {t('aiModelBuilder.student.accuracy', { value: accuracy })}
                        </Typography.Text>
                        {accuracy === 100 && (
                          <Typography.Text type="success" className="aimb-success-message">
                            {t('aiModelBuilder.student.messages.trainingSuccess')}
                          </Typography.Text>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
};

export default AIModelBuilderLessonPlayer;
